import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../services/api.ts';

type VideoItem = {
  id: number;
  name?: string | null;
  status: string;
  video_url: string | null;
  start_frame_edited_url?: string | null;
  end_frame_edited_url?: string | null;
  created_at?: string | null;
  error_message?: string | null;
};

const VideosLibrary: React.FC = () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const {
    data: videos = [],
    isLoading,
    error,
  } = useQuery<VideoItem[]>({
    queryKey: ['videos-library'],
    queryFn: async () => {
      const res = await apiRequest('/api/frame-to-video-generations');
      const items = (res.data || []).filter((v: any) => !!v.video_url);
      return items;
    },
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  // Orientation & resolution
  const [selectedOrientation, setSelectedOrientation] = useState<'landscape' | 'portrait'>('landscape');
  const [selectedResolution, setSelectedResolution] = useState<string>('854x480');
  const [selectedDuration, setSelectedDuration] = useState<'3s' | '5s' | '7s'>('7s');

  // Start frame source
  const [startSource, setStartSource] = useState<'upload' | 'library'>('upload');
  const [startFile, setStartFile] = useState<File | null>(null);
  const [startLibraryUrl, setStartLibraryUrl] = useState<string | null>(null);

  // End frame source
  const [endSource, setEndSource] = useState<'upload' | 'library'>('upload');
  const [endFile, setEndFile] = useState<File | null>(null);
  const [endLibraryUrl, setEndLibraryUrl] = useState<string | null>(null);

  // Available images for library pick
  const [chooseImages, setChooseImages] = useState<{ id: string; url: string; generationId?: number; createdAt?: string }[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState<boolean>(false);

  // View modal
  const [viewVideoUrl, setViewVideoUrl] = useState<string | null>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // No-op. List handled by useQuery.
  }, []);

  const openCreateModal = async () => {
    setShowCreateModal(true);
    setPrompt('');
    setSelectedOrientation('landscape');
    setSelectedResolution('854x480');
    setStartSource('upload');
    setEndSource('upload');
    setStartFile(null);
    setEndFile(null);
    setStartLibraryUrl(null);
    setEndLibraryUrl(null);
    try {
      setIsLoadingLibrary(true);
      const imagesRes = await apiRequest('/api/user-images');
      setChooseImages(imagesRes.data || []);
    } catch {
      setChooseImages([]);
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const handleDelete = async (id: number) => {
    const prev = queryClient.getQueryData<VideoItem[]>(['videos-library']) || [];
    queryClient.setQueryData<VideoItem[]>(['videos-library'], (cur) => (cur || []).filter((v) => v.id !== id));
    try {
      await apiRequest(`/api/frame-to-video-generations/${id}`, { method: 'DELETE' });
    } catch (e) {
      queryClient.setQueryData(['videos-library'], prev);
      alert('Failed to delete video');
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('Please enter a video prompt');
      return;
    }
    // start image required
    if (startSource === 'upload' && !startFile) {
      alert('Please upload a start image');
      return;
    }
    if (startSource === 'library' && !startLibraryUrl) {
      alert('Please select a start image');
      return;
    }
    // end image required
    if (endSource === 'upload' && !endFile) {
      alert('Please upload an end image');
      return;
    }
    if (endSource === 'library' && !endLibraryUrl) {
      alert('Please select an end image');
      return;
    }

    setIsGenerating(true);
    closeCreateModal();

    // Optimistic generating card
    const tempId = Date.now();
    const prev = queryClient.getQueryData<VideoItem[]>(['videos-library']) || [];
    queryClient.setQueryData<VideoItem[]>(['videos-library'], [
      {
        id: tempId,
        name: 'Generating...',
        status: 'generating',
        video_url: null,
        start_frame_edited_url: startLibraryUrl || (startFile ? URL.createObjectURL(startFile) : null),
        created_at: new Date().toISOString(),
      },
      ...prev,
    ]);

    try {
      // Prepare payload
      let startBase64: string | null = null;
      let endBase64: string | null = null;
      if (startSource === 'upload' && startFile) {
        startBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(startFile);
        });
      }
      if (endSource === 'upload' && endFile) {
        endBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(endFile);
        });
      }

      await apiRequest('/api/video-generation', {
        method: 'POST',
        body: JSON.stringify({
          start_frame: startSource === 'library' ? startLibraryUrl : null,
          end_frame: endSource === 'library' ? endLibraryUrl : null,
          start_frame_base64: startBase64,
          end_frame_base64: endBase64,
          video_prompt: prompt,
          orientation: selectedOrientation,
          resolution: selectedResolution,
          duration_frames: selectedDuration === '3s' ? 49 : selectedDuration === '5s' ? 81 : 113,
        }),
      });

      await queryClient.invalidateQueries({ queryKey: ['videos-library'] });
    } catch (e) {
      queryClient.setQueryData<VideoItem[]>(['videos-library'], (cur) => (cur || []).filter((v) => v.id !== tempId));
      alert('Failed to generate video');
    } finally {
      setIsGenerating(false);
    }
  };

  const headerTitle = useMemo(() => 'AI Videos', []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      <div className="container mx-auto px-6 py-8">
          {/* Back */}
          <div className="mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back to Home</span>
            </button>
          </div>

          {/* Header - match Characters/Scenes style */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-2">
              <h1 className="text-4xl font-bold text-gray-800">{headerTitle}</h1>
              <button
                onClick={openCreateModal}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-3 hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:scale-105 shadow-lg"
                title="Create New Video"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
            <p className="text-gray-600">Your videos library</p>
          </div>

          {/* Content */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-6">{error}</div>
          )}

          {isLoading ? (
            <div className="min-h-[50vh] flex items-center justify-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                <p className="text-gray-600">Loading videos...</p>
              </div>
            </div>
          ) : videos.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-12 text-center">
              <div className="text-6xl mb-4">🎞️</div>
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">No videos yet</h2>
              <p className="text-gray-600 mb-6">Create your first video by clicking the New button.</p>
              <button
                onClick={openCreateModal}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                + Create Video
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {videos.map((video) => (
                <div key={video.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                  <div
                    className="aspect-video bg-gray-100 flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      if (video.video_url) setViewVideoUrl(video.video_url);
                    }}
                    title={video.video_url ? 'View video' : undefined}
                  >
                    {video.video_url ? (
                      <video
                        src={video.video_url}
                        className="max-w-full max-h-full object-contain"
                        poster={video.start_frame_edited_url || undefined}
                        controls={false}
                      />
                    ) : (
                      <div className="text-gray-400 text-sm">{video.status === 'generating' ? 'Generating...' : 'No preview'}</div>
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800 truncate max-w-[12rem]">{video.name || `Video #${video.id}`}</div>
                      <div className="text-xs text-gray-500">{video.created_at ? new Date(video.created_at).toLocaleString() : ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {video.status === 'generating' && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      )}
                      <button
                        onClick={() => handleDelete(video.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowCreateModal(false);
          }}
        >
          <div ref={modalRef} className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">Create Video</h3>
              <button className="text-gray-500 hover:text-gray-700 text-2xl" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <div className="p-5 overflow-auto">
              {/* Start frame */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Starting Frame</h4>
                <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-3">
                  <button
                    className={`py-2 px-4 rounded-md text-sm font-medium ${startSource === 'upload' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setStartSource('upload')}
                  >
                    Upload
                  </button>
                  <button
                    className={`py-2 px-4 rounded-md text-sm font-medium ${startSource === 'library' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setStartSource('library')}
                  >
                    From Library
                  </button>
                </div>
                {startSource === 'upload' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 cursor-pointer" onClick={() => document.getElementById('video-start-upload')?.click()}>
                    <input id="video-start-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setStartFile(e.target.files?.[0] || null)} />
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📷</span>
                    </div>
                    <p className="text-gray-600 mb-2 text-center">Click to upload starting frame</p>
                    {!!startFile && (
                      <div className="mt-3">
                        <img src={URL.createObjectURL(startFile)} alt="Selected start upload" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {isLoadingLibrary ? (
                      <div className="text-center text-gray-600 py-10">Loading images...</div>
                    ) : chooseImages.length === 0 ? (
                      <div className="text-center text-gray-500 py-10">No images found</div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {chooseImages.map((img) => (
                          <div key={img.id} className={`border rounded-md overflow-hidden cursor-pointer ${startLibraryUrl === img.url ? 'border-blue-500' : 'border-gray-200'}`} onClick={() => setStartLibraryUrl(img.url)}>
                            <img src={img.url} alt={`Library ${img.generationId || ''}`} className="w-full h-28 object-contain" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {startSource === 'library' && startLibraryUrl && (
                  <div className="mt-3">
                    <img src={startLibraryUrl} alt="Selected start preview" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                  </div>
                )}
              </div>

              {/* End frame */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-800 mb-2">Ending Frame</h4>
                <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-3">
                  <button
                    className={`py-2 px-4 rounded-md text-sm font-medium ${endSource === 'upload' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setEndSource('upload')}
                  >
                    Upload
                  </button>
                  <button
                    className={`py-2 px-4 rounded-md text-sm font-medium ${endSource === 'library' ? 'bg-white text-green-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setEndSource('library')}
                  >
                    From Library
                  </button>
                </div>
                {endSource === 'upload' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-green-400 hover:bg-green-50 transition-all duration-200 cursor-pointer" onClick={() => document.getElementById('video-end-upload')?.click()}>
                    <input id="video-end-upload" type="file" accept="image/*" className="hidden" onChange={(e) => setEndFile(e.target.files?.[0] || null)} />
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">🎬</span>
                    </div>
                    <p className="text-gray-600 mb-2 text-center">Click to upload ending frame</p>
                    {!!endFile && (
                      <div className="mt-3">
                        <img src={URL.createObjectURL(endFile)} alt="Selected end upload" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {isLoadingLibrary ? (
                      <div className="text-center text-gray-600 py-10">Loading images...</div>
                    ) : chooseImages.length === 0 ? (
                      <div className="text-center text-gray-500 py-10">No images found</div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {chooseImages.map((img) => (
                          <div key={img.id} className={`border rounded-md overflow-hidden cursor-pointer ${endLibraryUrl === img.url ? 'border-green-500' : 'border-gray-200'}`} onClick={() => setEndLibraryUrl(img.url)}>
                            <img src={img.url} alt={`Library ${img.generationId || ''}`} className="w-full h-28 object-contain" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {endSource === 'library' && endLibraryUrl && (
                  <div className="mt-3">
                    <img src={endLibraryUrl} alt="Selected end preview" className="w-full h-40 object-contain rounded-lg border border-gray-200" />
                  </div>
                )}
              </div>

              {/* Orientation & Resolution */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <select
                    value={selectedOrientation}
                    onChange={(e) => {
                      const v = e.target.value as 'landscape' | 'portrait';
                      setSelectedOrientation(v);
                      // Reset resolution default to match orientation
                      setSelectedResolution(v === 'landscape' ? '854x480' : '480x854');
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resolution</label>
                  <select
                    value={selectedResolution}
                    onChange={(e) => setSelectedResolution(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {selectedOrientation === 'landscape' ? (
                      <>
                        <option value="854x480">854 x 480 (Default)</option>
                        <option value="1024x576">1024 x 576</option>
                        <option value="1280x720">1280 x 720</option>
                      </>
                    ) : (
                      <>
                        <option value="480x854">480 x 854 (Default)</option>
                        <option value="576x1024">576 x 1024</option>
                        <option value="720x1280">720 x 1280</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <select
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(e.target.value as '3s' | '5s' | '7s')}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="3s">3 seconds</option>
                    <option value="5s">5 seconds</option>
                    <option value="7s">7 seconds</option>
                  </select>
                </div>
              </div>

              {/* Prompt */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Video Prompt</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the transition and movement..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-3">
              <button className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button
                className={`px-5 py-2 rounded-lg text-white font-semibold ${isGenerating ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                onClick={handleGenerate}
                disabled={
                  isGenerating ||
                  !prompt.trim() ||
                  (startSource === 'upload' && !startFile) || (startSource === 'library' && !startLibraryUrl) ||
                  (endSource === 'upload' && !endFile) || (endSource === 'library' && !endLibraryUrl)
                }
              >
                {isGenerating ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Video Modal */}
      {viewVideoUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setViewVideoUrl(null);
          }}
        >
          <div className="bg-white rounded-xl max-w-5xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">View Video</h3>
              <button onClick={() => setViewVideoUrl(null)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>
            <div className="p-4 max-h-[75vh] overflow-auto">
              <video src={viewVideoUrl} className="max-w-full max-h-[70vh] object-contain rounded" controls />
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = viewVideoUrl;
                  link.download = `video-${Date.now()}.mp4`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold"
              >
                Download
              </button>
              <button onClick={() => setViewVideoUrl(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosLibrary;


